import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Flame, BarChart3, RefreshCw } from 'lucide-react';
import StatCard from '../../components/cards/StatCard';
import PriceChart from '../../components/charts/PriceChart';
import { getMarketMovers, getMarketSummary } from '../../api/dashboard';

export default function MarketMovers({ onCardClick }: { onCardClick?: (id: string) => void }) {
  const [period, setPeriod] = useState('7d');
  const [direction, setDirection] = useState('all');
  const [movers, setMovers] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getMarketMovers(period, 30, direction),
      getMarketSummary(),
    ]).then(([moversData, summaryData]) => {
      setMovers(moversData.movers || []);
      setSummary(summaryData);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, [period, direction]);

  const topUp = movers.filter(m => m.variation_pct > 0)[0];
  const topDown = movers.filter(m => m.variation_pct < 0).sort((a, b) => a.variation_pct - b.variation_pct)[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Market Movers</h2>
          <p className="text-sm text-neutral-500 mt-1">Cartes avec les plus fortes variations de prix</p>
        </div>
        <div className="flex gap-2">
          {['24h', '7d', '30d'].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${period === p ? 'bg-accent text-white' : 'bg-surface text-neutral-400 hover:bg-neutral-800'}`}>
              {p}
            </button>
          ))}
          <div className="w-px bg-border mx-1" />
          {[{id:'all',label:'Tous'},{id:'up',label:'Hausse'},{id:'down',label:'Baisse'}].map(d => (
            <button key={d.id} onClick={() => setDirection(d.id)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${direction === d.id ? 'bg-accent text-white' : 'bg-surface text-neutral-400 hover:bg-neutral-800'}`}>
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Cartes avec prix" value={summary?.with_price?.toLocaleString() || '—'} subtitle="sources actives" icon={<BarChart3 size={16} />} />
        <StatCard label="Prix moyen" value={summary ? `${summary.avg_price}€` : '—'} subtitle={`médiane: ${summary?.median_price || '?'}€`} />
        {topUp && <StatCard label="Top Hausse" value={`+${topUp.variation_pct}%`} subtitle={topUp.name_fr || topUp.name} trend={topUp.variation_pct} icon={<TrendingUp size={16} />} />}
        {topDown && <StatCard label="Top Baisse" value={`${topDown.variation_pct}%`} subtitle={topDown.name_fr || topDown.name} trend={topDown.variation_pct} icon={<TrendingDown size={16} />} />}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-20 text-neutral-500">Chargement...</div>
      ) : (
        <div className="bg-surface border border-border rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 text-xs text-neutral-500 uppercase">Carte</th>
                <th className="text-right px-4 py-3 text-xs text-neutral-500 uppercase">Prix actuel</th>
                <th className="text-right px-4 py-3 text-xs text-neutral-500 uppercase">Ancien prix</th>
                <th className="text-right px-4 py-3 text-xs text-neutral-500 uppercase">Variation</th>
              </tr>
            </thead>
            <tbody>
              {movers.map((card, i) => (
                <tr key={`${card.id}-${i}`} onClick={() => onCardClick?.(card.id)} className="border-b border-border/50 hover:bg-neutral-800/50 transition-colors cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={`${process.env.REACT_APP_API_URL || ''}/img/opus-assets/cards/${card.id}.png`} alt="" className="w-8 h-11 rounded flex-shrink-0 object-cover bg-neutral-700" />
                      <div>
                        <div className="text-sm font-medium text-white">
                          {card.name_fr || card.name}
                          {card.variant_name && card.variant_name !== 'Normal' && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-accent/10 text-accent rounded">{card.variant_name}</span>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500">{card.set_name || card.set_id} — {card.rarity}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-semibold text-white">{Number(card.current_price).toFixed(2)}€</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm text-neutral-400">{Number(card.old_price).toFixed(2)}€</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${Number(card.variation_pct) > 0 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      {Number(card.variation_pct) > 0 ? '+' : ''}{Number(card.variation_pct).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
